import React, { useState } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { CompletenessResult, SectionCompleteness, FieldCompleteness } from '@lds-efsp/shared-contracts';

interface CompletenessDashboardProps {
  brand: BrandConfig;
  completeness: CompletenessResult;
  onNavigateToSection?: (section: string) => void;
}

const CompletenessDashboard: React.FC<CompletenessDashboardProps> = ({
  brand,
  completeness,
  onNavigateToSection,
}) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const { totalFields, completedFields, percentage, sections } = completeness;
  const isComplete = percentage === 100;

  const progressColor = isComplete
    ? brand.colors.success || '#22C55E'
    : percentage >= 50
      ? brand.colors.warning || '#F59E0B'
      : '#EF4444';

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        borderColor: brand.colors.border,
        backgroundColor: brand.colors.bgLight,
      }}
    >
      <h4
        className="text-xs font-semibold uppercase tracking-wide mb-3"
        style={{ color: brand.colors.textLight }}
      >
        Completeness
      </h4>

      {/* Overall progress */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium" style={{ color: brand.colors.text }}>
            {completedFields}/{totalFields} fields
          </span>
          <span
            className="text-sm font-bold"
            style={{ color: progressColor }}
          >
            {percentage}%
          </span>
        </div>
        <div
          className="w-full h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: brand.colors.border }}
        >
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${percentage}%`,
              backgroundColor: progressColor,
            }}
          />
        </div>
      </div>

      {/* Per-section rows */}
      <div className="space-y-1">
        {sections.map((section) => (
          <SectionRow
            key={section.section}
            brand={brand}
            section={section}
            fields={completeness.fields.filter(f => f.section === section.section)}
            isExpanded={expandedSection === section.section}
            onToggle={() => setExpandedSection(
              expandedSection === section.section ? null : section.section,
            )}
            onNavigate={onNavigateToSection}
          />
        ))}
      </div>
    </div>
  );
};

interface SectionRowProps {
  brand: BrandConfig;
  section: SectionCompleteness;
  fields: FieldCompleteness[];
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate?: (section: string) => void;
}

const SectionRow: React.FC<SectionRowProps> = ({
  brand,
  section,
  fields,
  isExpanded,
  onToggle,
  onNavigate,
}) => {
  const isComplete = section.percentage === 100;
  const hasErrors = section.errorCount > 0;
  const hasWarnings = section.warningCount > 0;
  const incompleteFields = fields.filter(f => f.status !== 'complete');

  const statusColor = isComplete
    ? brand.colors.success || '#22C55E'
    : hasErrors
      ? '#EF4444'
      : brand.colors.textLight;

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-1.5 px-2 rounded hover:bg-black/5 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* Status indicator */}
          <span
            className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
            style={{
              backgroundColor: isComplete ? (brand.colors.success || '#22C55E') : 'transparent',
              border: isComplete ? 'none' : `2px solid ${statusColor}`,
              color: isComplete ? '#FFFFFF' : statusColor,
            }}
          >
            {isComplete ? '\u2713' : ''}
          </span>

          <span
            className="text-xs font-medium truncate"
            style={{ color: brand.colors.text }}
          >
            {section.label}
          </span>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Error badge */}
          {hasErrors && (
            <span
              className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
              style={{ backgroundColor: '#EF4444' }}
            >
              {section.errorCount}
            </span>
          )}
          {/* Warning badge */}
          {hasWarnings && (
            <span
              className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
              style={{ backgroundColor: brand.colors.warning || '#F59E0B' }}
            >
              {section.warningCount}
            </span>
          )}
          {/* Mini progress */}
          <span
            className="text-[10px] font-medium"
            style={{ color: statusColor }}
          >
            {section.completedCount}/{section.fieldCount}
          </span>
          {/* Expand arrow */}
          <span
            className="text-[10px] transition-transform"
            style={{
              color: brand.colors.textLight,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            &#9660;
          </span>
        </div>
      </button>

      {/* Expanded field list */}
      {isExpanded && incompleteFields.length > 0 && (
        <div className="ml-6 mt-1 mb-2 space-y-0.5">
          {incompleteFields.map((field) => (
            <button
              key={field.field}
              onClick={() => onNavigate?.(section.section)}
              className="flex items-center gap-1.5 w-full text-left py-0.5 hover:underline"
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: field.status === 'error'
                    ? '#EF4444'
                    : field.status === 'warning'
                      ? (brand.colors.warning || '#F59E0B')
                      : brand.colors.textLight,
                }}
              />
              <span
                className="text-[11px]"
                style={{ color: brand.colors.textLight }}
              >
                {field.label}
              </span>
              {field.message && (
                <span
                  className="text-[10px] italic"
                  style={{ color: brand.colors.textLight }}
                >
                  — {field.message}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {isExpanded && incompleteFields.length === 0 && (
        <p
          className="ml-6 mt-1 mb-2 text-[11px]"
          style={{ color: brand.colors.success || '#22C55E' }}
        >
          All fields complete
        </p>
      )}
    </div>
  );
};

export default CompletenessDashboard;
